import {
  ChangeCircle,
  Delete,
  Download,
  Edit,
  History,
  OpenInNew,
  Save,
} from "@mui/icons-material";
import FileIcon from "./FileIcon";


import {
  Avatar,
  CircularProgress,
  Collapse,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { processesUploadsApi, uploadsApi } from "api";
import { useSnackbar, useTranslations, useUser, useSelectedOrganization } from "hooks";

import React, { useEffect, useState } from "react";
import { getTranslation } from "common";
import {
  SUPPORTED_EXTENSIONS,
  formatDate,
  getFileExtension,
  toBase64,
} from "common/utilities";
import { Box } from "@mui/system";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import FilePreviewDialog from "./dialogs/FilePreviewDialog";
import ConfirmDialog from "./dialogs/ConfirmDialog";

export interface Attachment {
  id: number;
  idorganization: number;
  filename: string;
  description: string;
  idtype: number;
  cloudlink: string;
  creation: string;
  last_modified: string | null;
  foldername: string;
  username: string;
  iduser?: number;
}

interface AttchmentListProps {
  attachments: Attachment[];
  canManage?: boolean;
  knownWidth: number;
  horizontal?: boolean;
  getAttachments: () => Promise<void>;
  images: { [key: string]: string };
  histories: { [key: string]: any };
}

const AttachmentList: React.FC<AttchmentListProps> = ({
  attachments,
  knownWidth,
  horizontal,
  getAttachments,
  images,
  histories,
  canManage = false,
}) => {

  const [idItem, setIdItem] = useState<number | null>(null);
  const download = async (attachment: Attachment | any, useUploadsApi = false, mainAttachmentId?: number) => {
    setLoading(true);
    setIdItem(attachment.id);
    try {
      const api = useUploadsApi ? uploadsApi : processesUploadsApi;
      const endpoint = (useUploadsApi && mainAttachmentId) 
        ? `/${mainAttachmentId}/histories/${attachment.id}` 
        : `/${attachment.id}`;
      const { data } = await api.get(endpoint);
      const a = document.createElement("a");
      a.href = data.blob;
      a.download = attachment.filename || data.filename;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
    } catch {
    } finally {
      setLoading(false);
    }
  };

  let organizationId: number | null | undefined = null;
  const { sendSnack } = useSnackbar();
  const translations = useTranslations();

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [edit, setEdit] = useState<Attachment | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<{ id: number; filename: string; useUploadsApi?: boolean; mainAttachmentId?: number } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Attachment | null>(null);

  const user = useUser();
  const selectedOrganization = useSelectedOrganization();

  /**
   * Returns true if the current user can modify the given attachment.
   * A user can modify if they are an org administrator or if they are the
   * attachment creator. Currently compares by username; will be migrated
   * to userId in the future — just change the comparison below.
   */
  const canModifyAttachment = (attachment: Attachment): boolean => {
    if (!user) return false;
    if (selectedOrganization?.administrator) return true;
    return attachment.iduser === user.userId;
  };

  useEffect(() => {
    if (edit) {
      document.getElementById(`input-description-${edit.id}`)?.focus();
    }
  }, [edit]);

  const handleDeleteFile = async (attachment: Attachment) => {
    try {
      const deleteApi = await processesUploadsApi.delete(`/${attachment.id}`);
      sendSnack({
        message: `${deleteApi.data.responsemessage}`,
        type: "success",
      });
      getAttachments();
    } catch {
    } finally {
    }
  };

  const handleUpdateFile = async (
    attachment: Attachment,
    files: FileList | null
  ) => {
    setLoading(true);
    try {
      if (files && files.length > 0) {
        const file = files[0];
        const ext = getFileExtension(file.name);
        if (!ext || !SUPPORTED_EXTENSIONS.includes(ext.toLowerCase())) {
          sendSnack({
            message: getTranslation(translations, "attachment.extension.unsupported") || `L'estensione del file ${file.name} non è supportata.`,
            type: "error",
          });
          setLoading(false);
          return;
        }

        const payload = {
          idorganization: organizationId,
          filename: file.name,
          cloudlink: "",
          idtype: 1,
          description: file.name,
          blob: await toBase64(file),
        };
        setLoading(true);
        setIdItem(attachment.id);
        try {
          const updateApi = await uploadsApi.put(`/${attachment.id}`, payload);
          sendSnack({
            message: `${updateApi.data.responsemessage}`,
            type: "success",
          });
        } catch {
        } finally {
          setLoading(false);
        }
        getAttachments();
      }
    } finally {
    }
  };

  const isFromGoogleDrive = (idtype: number) => {
    return [20, 21, 22, 23, 24, 25].includes(idtype);
  };

  const saveEdit = async (e?: any) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    if (!edit) return;

    setLoading(true);
    setIdItem(edit.id || null);

    // Re-append extension from filename if the description was edited as a basename
    const attachment = attachments.find(a => a.id === edit.id);
    const fileName = attachment?.filename || "";
    const lastDotIndex = fileName.lastIndexOf(".");
    const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : "";
    const finalDescription = edit.description.endsWith(extension) && extension !== ""
      ? edit.description
      : edit.description + extension;

    try {
      await processesUploadsApi.put(`${edit.id}`, {
        description: finalDescription,
      });

      getAttachments();
      setEdit(null);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setEdit(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [setEdit, saveEdit]);



  return horizontal ? (
    <Stack direction="row" spacing={1} flex={1}>
      <Stack
        direction="row"
        position="relative"
        sx={{
          overflowX: "auto",
        }}
        width="100%"
      >
        {attachments
          .sort(
            (a, b) =>
              new Date(b.creation).getTime() - new Date(a.creation).getTime()
          )
          .map((attachment) => (
            <Tooltip
              key={`horizontal-attachment-${attachment.id}`}
              title={attachment.filename}
            >
              <Stack maxWidth={50} direction="column" alignItems="center">
                <FormHelperText
                  id="attachment-list"
                  sx={{
                    textOverflow: "ellipsis",
                    maxWidth: 50,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {attachment.filename}
                </FormHelperText>
              </Stack>
            </Tooltip>
          ))}
      </Stack>
    </Stack>
  ) : (
    <Stack direction="column" spacing={1} flex={1} width="100%">
      {attachments.length > 0 && (
        <Stack
          component={List}
          direction="column"
          position="relative"
          width="100%"
        >
          {attachments
            .sort(
              (a, b) =>
                new Date(b.creation).getTime() - new Date(a.creation).getTime()
            )
            .map((attachment) => (
              <React.Fragment key={`vertical-attachment-${attachment.id}`}>
                <ListItem key={`attachment-${attachment.id}`} id={`attachment-${attachment.id}`} dense>
                  <ListItemAvatar
                    onClick={() => {
                      if (!isFromGoogleDrive(attachment.idtype)) {
                        setPreviewAttachment({ id: attachment.id, filename: attachment.filename });
                      }
                    }}
                    sx={{
                      cursor: isFromGoogleDrive(attachment.idtype) ? "default" : "pointer",
                    }}
                  >
                    <Tooltip
                      title={!isFromGoogleDrive(attachment.idtype) ? (getTranslation(translations, "attachment.preview.label") || "Anteprima non disponibile") : ""}
                      placement="top"
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          bgcolor: "#54bdc1",
                          color: "#fff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                          "& .MuiSvgIcon-root": { fontSize: "2rem" }
                        }}

                        alt="..."
                        src={
                          SUPPORTED_EXTENSIONS.includes(
                            getFileExtension(attachment.filename) || ""
                          )
                            ? images[attachment.id]
                            : undefined
                        }
                      >
                        <FileIcon filename={attachment.filename} idtype={attachment.idtype} />
                      </Avatar>


                    </Tooltip>
                  </ListItemAvatar>
                  <ListItemText
                    sx={{
                      textOverflow: "ellipsis",
                      maxWidth: "auto",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}
                    primary={
                      <Box display="flex" flexDirection="column">
                        {/* Prima riga: input descrizione + tutte le icone */}
                        <Stack
                          component="form"
                          direction="row"
                          alignItems="center"
                          spacing={3}
                        >
                          <Box flex={1} minWidth={0} display="flex" alignItems="center">
                            <input
                              id={`input-description-${attachment.id}`}
                              style={{
                                border: "none",
                                background: "transparent",
                                width: edit?.id === attachment.id ? "auto" : "100%",
                                flex: edit?.id === attachment.id ? 1 : "none",
                                fontSize: "16px",
                                fontWeight: 600,
                                fontFamily: "Poppins-Light",
                                outline: edit?.id === attachment.id ? "1px solid #54bdc1" : "none",
                                borderRadius: 2,
                              }}
                              value={
                                edit?.id !== attachment.id
                                  ? attachment.description
                                  : edit.description
                              }
                              onChange={({ target }) =>
                                setEdit((prev) => ({
                                  ...(prev as Attachment),
                                  description: target.value,
                                }))
                              }
                              disabled={edit?.id !== attachment.id}
                              onBlur={() => {
                                // Salva solo se stiamo editando questo attachment
                                if (edit?.id === attachment.id) {
                                  saveEdit({});
                                }
                              }}
                            />
                            {edit?.id === attachment.id && (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  fontWeight: 600,
                                  fontFamily: "Poppins-Light",
                                  ml: 0.5,
                                  whiteSpace: "nowrap"
                                }}
                              >
                                {attachment.filename.lastIndexOf(".") !== -1
                                  ? attachment.filename.substring(attachment.filename.lastIndexOf("."))
                                  : ""}
                              </Typography>
                            )}
                          </Box>



                          {/* Edit/Save */}
                          {canManage && (
                            <Tooltip
                              title={
                                edit?.id !== attachment.id
                                  ? getTranslation(translations, "pm.button.edit")
                                  : getTranslation(translations, "pm.button.save")
                              }
                            >
                              <span
                                onClick={() => {
                                  if (!canModifyAttachment(attachment)) {
                                    sendSnack({
                                      message: getTranslation(translations, "attachment.permission.denied") || "Solo gli amministratori o il proprietario dell'allegato possono eseguire questa azione.",
                                      type: "warning",
                                    });
                                  }
                                }}
                              >
                                <IconButton
                                  onClick={(e) => {
                                    e.preventDefault();
                                    if (edit?.id !== attachment.id) {
                                      const lastDotIndex = attachment.description.lastIndexOf(".");
                                      const baseName = lastDotIndex !== -1 ? attachment.description.substring(0, lastDotIndex) : attachment.description;
                                      setEdit({
                                        ...attachment,
                                        description: baseName
                                      });
                                    } else {
                                      saveEdit(e);
                                    }
                                  }}
                                  disabled={!canModifyAttachment(attachment)}
                                  size="medium"
                                  type={
                                    edit?.id !== attachment.id ? "button" : "submit"
                                  }
                                  sx={{ p: 0.25 }}
                                >
                                  {edit?.id !== attachment.id ? (
                                    <Edit fontSize="inherit" color={canModifyAttachment(attachment) ? "primary" : "disabled"} />
                                  ) : (
                                    <Save fontSize="inherit" color={canModifyAttachment(attachment) ? "primary" : "disabled"} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Delete */}
                          {canManage && (
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "settings.delete.label"
                              )}
                            >
                              <span
                                onClick={() => {
                                  if (!canModifyAttachment(attachment)) {
                                    sendSnack({
                                      message: getTranslation(translations, "attachment.permission.denied") || "Solo gli amministratori o il proprietario dell'allegato possono eseguire questa azione.",
                                      type: "warning",
                                    });
                                  }
                                }}
                              >
                                <IconButton
                                  id={`delete-attachment-${attachment.id}`}
                                  size="medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setDeleteTarget(attachment);
                                  }}
                                  disabled={!canModifyAttachment(attachment)}
                                  sx={{ p: 0.25 }}
                                >
                                  <Delete fontSize="inherit" color={canModifyAttachment(attachment) ? "primary" : "disabled"} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {/* Google Drive: Open in new */}
                          {isFromGoogleDrive(attachment.idtype) ? (
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "attachment.view.ondrive"
                              )}
                            >
                              <IconButton
                                id={`open-attachment-${attachment.id}`}
                                size="medium"
                                component="a"
                                href={attachment.cloudlink}
                                target="_blank"
                                sx={{ p: 0.25 }}
                              >
                                <OpenInNew fontSize="inherit" color="primary" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <>
                              {/* Upload new version */}
                              {canManage && !attachment.cloudlink && (
                                <Tooltip
                                  title={getTranslation(
                                    translations,
                                    "attachments.new_version.label"
                                  )}
                                >
                                  <span
                                    onClick={() => {
                                      if (!canModifyAttachment(attachment)) {
                                        sendSnack({
                                          message: getTranslation(translations, "attachment.permission.denied") || "Solo gli amministratori o il proprietario dell'allegato possono eseguire questa azione.",
                                          type: "warning",
                                        });
                                      }
                                    }}
                                  >
                                    <IconButton
                                      id={`modify-attachment-${attachment.id}`}
                                      size="medium"
                                      component="label"
                                      disabled={!canModifyAttachment(attachment)}
                                      sx={{ p: 0.25 }}
                                    >
                                      <ChangeCircle fontSize="inherit" color={canModifyAttachment(attachment) ? "primary" : "disabled"} />
                                      <input
                                        id={`modify-attachment-${attachment.id}-input`}
                                        type="file"
                                        hidden
                                        accept={SUPPORTED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
                                        onChange={({ target }) => {
                                          handleUpdateFile(attachment, target.files);
                                          target.value = "";
                                        }}
                                      />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              )}

                              {/* Download */}
                              <Tooltip
                                title={getTranslation(
                                  translations,
                                  "settings.download.label"
                                )}
                              >
                                <IconButton
                                  id={`download-attachment-${attachment.id}`}
                                  size="medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    download(attachment);
                                  }}
                                  sx={{ p: 0.25 }}
                                >
                                  <Download fontSize="inherit" color="primary" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}

                          {/* History */}
                          {histories[attachment.id].length > 0 && (
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "settings.histories.label"
                              )}
                            >
                              <IconButton
                                id={`history-button-${attachment.id}`}
                                size="medium"
                                onClick={() => {
                                  setSelectedId(
                                    selectedId === attachment.id
                                      ? null
                                      : attachment.id
                                  );
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <History fontSize="inherit" color="primary" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>

                        {/* Seconda riga: username e data */}
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ color: "text.secondary" }}
                        >
                          {attachment.username}, {formatDate(attachment.creation)}
                        </Typography>
                      </Box>
                    }
                  />
                  {loading && idItem == attachment.id && (
                    <Stack alignItems="center">
                      <CircularProgress />
                    </Stack>
                  )}
                </ListItem>

                {/* History collapse */}
                {histories[attachment.id].length > 0 && (
                  <Collapse in={selectedId === attachment.id}>
                    <List sx={{ width: "100%", pl: 6 }} dense>
                      {histories[attachment.id].map(
                        (history: any, index: any) => (
                          <ListItem
                            id={`attachment-history-${history.id}`}
                            key={index}
                          >
                            <ListItemAvatar
                              onClick={() => {
                                setPreviewAttachment({ id: history.id, filename: history.filename, useUploadsApi: true, mainAttachmentId: attachment.id });
                              }}
                              sx={{ cursor: "pointer" }}
                            >
                              <Tooltip
                                title={getTranslation(translations, "attachment.preview.label") || "Anteprima non disponibile"}
                                placement="top"
                              >
                                <Avatar
                                  sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: "#54bdc1",
                                    color: "#fff",
                                  }}
                                >
                                  <FileIcon filename={history.filename} fontSize="small" />
                                </Avatar>
                              </Tooltip>
                            </ListItemAvatar>
                            <ListItemText
                              sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                              }}
                              primary={`${getTranslation(
                                translations,
                                "settings.filename.label"
                              )}: ${history.filename}`}
                              secondary={`${getTranslation(
                                translations,
                                "settings.creation.label"
                              )}: ${history.nameuser}, ${formatDate(
                                history.creation
                              )}`}
                            />
                            <Tooltip
                              title={getTranslation(
                                translations,
                                "settings.download.label"
                              )}
                            >
                              <IconButton
                                id={`download-history-${history.id}`}
                                size="medium"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  download(history, true, attachment.id);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <Download fontSize="inherit" color="primary" />
                              </IconButton>
                            </Tooltip>
                          </ListItem>
                        )
                      )}
                    </List>
                  </Collapse>
                )}

              </React.Fragment>
            ))}

        </Stack>
      )}

      {/* File Preview Dialog */}
      <FilePreviewDialog
        attachmentId={previewAttachment?.id ?? null}
        filename={previewAttachment?.filename ?? ""}
        useUploadsApi={previewAttachment?.useUploadsApi ?? false}
        mainAttachmentId={previewAttachment?.mainAttachmentId}
        onClose={() => setPreviewAttachment(null)}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={getTranslation(translations, "settings.delete.label") || "Elimina"}
        content={getTranslation(translations, "attachment.delete.confirm") || "Sei sicuro di voler eliminare questo allegato?"}
        cancelText={getTranslation(translations, "generic.no.caption") || "No"}
        confirmText={getTranslation(translations, "generic.yes.caption") || "Sì"}
        onConfirm={() => {
          if (deleteTarget) {
            handleDeleteFile(deleteTarget);
            setDeleteTarget(null);
          }
        }}
      />
    </Stack>
  );
};

export default AttachmentList;